<template>
    <div class="carnot-live-html-widget">
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <v-alert v-if="error" :value="true" type="error" class="ma-2">
            {{error}}
        </v-alert>
        <component v-if="templateStyles" :is="'style'" v-html="templateStyles"></component>
        <runtime-template-compiler
            v-if="template&&!error"
            :template="template"
            :parent="parentComponent"
            :key="templateRenderKey"
        ></runtime-template-compiler>
    </div>
</template>

<script>
import {callAPI} from '@/helper'
import LoadingProgressBar from './../LoadingProgressBar'
import {RuntimeTemplateCompiler} from 'vue-runtime-template-compiler'
import data3Processor from '../mixins/data3Processor'
import unitResolver from '../mixins/unitResolver'

export default {
    name: 'LiveHtmlWidget',
    components: {LoadingProgressBar, RuntimeTemplateCompiler},
    mixins: [data3Processor, unitResolver],
    props: {
        html: {type: String, default: ''},
        data3query: {type: Object, default: null},
        parameters: {type: Object, default: null},
        dateRange: {type: Object, default: null},
        equipment: {type: String, default: null},
        allowExternalStyles: {type: Boolean, default: false},
        allowScripts: {type: Boolean, default: false},
    },
    data() {
        return {
            loading: false,
            error: null,
            template: null,
            templateStyles: '',
            templateRenderKey: 0,
            points: {},
            latestValues: {},
            data: {},
            reloadInc: 0,
            refreshTimeout: null,
            parentComponent: this,
            resourceLinks: [],
            resourceScripts: [],
            injectedResources: [],
        }
    },
    watch: {
        html: {
            handler() {
                this.buildTemplate()
            },
        },
        data3query: {
            handler() {
                this.refresh()
            },
            deep: true,
        },
        dateRange: {
            handler() {
                this.refresh()
            },
            deep: true,
        },
        equipment() {
            this.refresh()
        },
        parameters: {
            handler() {
                this.refresh()
            },
            deep: true,
        },
        allowExternalStyles() {
            this.mountAllowedResources()
        },
        allowScripts() {
            this.mountAllowedResources()
        },
    },
    created() {
        this.buildTemplate()
    },
    mounted() {
        this.refresh()
    },
    beforeDestroy() {
        clearTimeout(this.refreshTimeout)
        this.clearInjectedResources()
        this.reloadInc += 1
    },
    errorCaptured(error) {
        this.error = 'Unable to render live HTML: ' + error.message
        return false
    },
    methods: {
        latestValue(seriesName, fallback = null) {
            if (Object.prototype.hasOwnProperty.call(this.latestValues, seriesName)) {
                return this.latestValues[seriesName]
            }
            let normalizedName = String(seriesName).toLowerCase()
            let matchingSeries = Object.keys(this.latestValues).find(name => {
                let normalizedSeries = name.toLowerCase()
                return normalizedSeries.endsWith(' - ' + normalizedName) || normalizedSeries.includes(normalizedName)
            })
            return matchingSeries ? this.latestValues[matchingSeries] : fallback
        },
        displayValue(seriesName, digits = null, fallback = '--') {
            let value = this.latestValue(seriesName, fallback)
            return typeof value === 'number' && digits !== null ? value.toFixed(digits) : value
        },
        percent(value, maximum) {
            if (typeof value !== 'number' || !maximum) {
                return '0%'
            }
            return Math.max(0, Math.min(value / maximum * 100, 100)) + '%'
        },
        buildTemplate() {
            this.error = null
            let isRebuild = this.template !== null
            let source = this.html || ''
            let styleMatches = source.match(/<style\b[^>]*>[\s\S]*?<\/style\s*>/gi) || []
            this.resourceLinks = source.match(/<link\b[^>]*>/gi) || []
            this.resourceScripts = source.match(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi) || []
            this.templateStyles = this.scopeDocumentStyles(styleMatches.map(style => style.replace(/^<style\b[^>]*>|<\/style\s*>$/gi, '')).join('\n'))
            source = source.replace(/<style\b[^>]*>[\s\S]*?<\/style\s*>/gi, '')
            source = source.replace(/<link\b[^>]*>/gi, '')
            let normalized = this.normalizeHtml(source)
            this.template = '<div class="live-html-document">' + normalized + '</div>'
            this.$nextTick(() => this.mountAllowedResources())
            if (isRebuild) {
                this.templateRenderKey += 1
            }
        },
        mountAllowedResources() {
            this.clearInjectedResources()
            if (!this.$el || typeof document === 'undefined') {
                return
            }
            if (this.allowExternalStyles) {
                for (let markup of this.resourceLinks) {
                    let sourceNode = this.parseResourceNode(markup, 'link')
                    if (!sourceNode || String(sourceNode.rel).toLowerCase() !== 'stylesheet' || !this.isAllowedResourceUrl(sourceNode.href)) {
                        continue
                    }
                    let link = document.createElement('link')
                    for (let attribute of Array.from(sourceNode.attributes)) {
                        link.setAttribute(attribute.name, attribute.value)
                    }
                    document.head.appendChild(link)
                    this.injectedResources.push(link)
                }
            }
            if (this.allowScripts) {
                for (let markup of this.resourceScripts) {
                    let sourceNode = this.parseResourceNode(markup, 'script')
                    if (!sourceNode || (sourceNode.src && !this.isAllowedResourceUrl(sourceNode.src))) {
                        continue
                    }
                    let script = document.createElement('script')
                    for (let attribute of Array.from(sourceNode.attributes)) {
                        script.setAttribute(attribute.name, attribute.value)
                    }
                    script.text = sourceNode.textContent
                    document.body.appendChild(script)
                    this.injectedResources.push(script)
                }
            }
        },
        parseResourceNode(markup, selector) {
            let container = document.createElement('div')
            container.innerHTML = markup
            return container.querySelector(selector)
        },
        isAllowedResourceUrl(value) {
            try {
                let url = new URL(value, window.location.href)
                return url.protocol === 'https:' || url.origin === window.location.origin
            } catch (error) {
                return false
            }
        },
        clearInjectedResources() {
            for (let resource of this.injectedResources) {
                if (resource.parentNode) {
                    resource.parentNode.removeChild(resource)
                }
            }
            this.injectedResources = []
        },
        scopeDocumentStyles(styles) {
            let wrapper = '.carnot-live-html-widget .live-html-document'
            return styles
                .replace(/(^|[},])\s*(?:html|body|:root)(?=\s|[,{.#[:>+~])/gi, '$1 ' + wrapper)
                .replace(/(^|[},])\s*\*(?=\s*\{)/g, '$1 :where(' + wrapper + ') *')
        },
        normalizeHtml(html) {
            if (!html) {
                return ''
            }
            let normalized = html.replace(/<!doctype[^>]*>/gi, '')
            normalized = normalized.replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '')
            normalized = normalized.replace(/<head\b[^>]*>[\s\S]*?<\/head\s*>/gi, '')
            let bodyMatch = normalized.match(/<body\b[^>]*>([\s\S]*?)<\/body\s*>/i)
            if (bodyMatch) {
                normalized = bodyMatch[1]
            }
            return normalized.replace(/<\/?html\b[^>]*>/gi, '').replace(/<\/?body\b[^>]*>/gi, '')
        },
        refresh() {
            clearTimeout(this.refreshTimeout)
            this.refreshTimeout = setTimeout(() => this.loadData(), 50)
        },
        async loadData() {
            this.reloadInc += 1
            let currentInc = this.reloadInc
            this.error = null
            if (!this.data3query || !Array.isArray(this.data3query['data-payload'])) {
                this.loading = false
                this.points = {}
                this.latestValues = {}
                this.data = {}
                this.templateRenderKey += 1
                return
            }

            this.loading = true
            try {
                let payloadArray = JSON.parse(JSON.stringify(this.data3query['data-payload']))
                payloadArray = payloadArray.filter(payload => payload.filters && (payload.filters.sensor_name || payload.filters.equipment_name))
                for (let payload of payloadArray) {
                    payload.parameters = this.parameters
                    this.processDateRange(payload, this.dateRange)
                    if (!payload.filters.facility_name && this.$store.state.pointsListStore.pointsList.facility_name) {
                        payload.filters.facility_name = this.$store.state.pointsListStore.pointsList.facility_name
                    }
                    if (this.equipment) {
                        this.processEquipment(payload, this.equipment)
                    }
                }

                let results = await Promise.all(payloadArray.map(payload => callAPI('data3/', payload)))
                if (currentInc !== this.reloadInc) {
                    return
                }

                let points = {}
                let latestValues = {}
                let data = {}
                for (let result of results) {
                    if (!result.data || result.data.error) {
                        continue
                    }
                    for (let seriesName in result.data) {
                        if (!data[seriesName]) {
                            data[seriesName] = result.data[seriesName]
                        }
                        if (seriesName === 'timestamp' || !Array.isArray(result.data[seriesName])) {
                            continue
                        }
                        let value = result.data[seriesName].reduce((latest, current) => current !== null ? current : latest, null)
                        let equipmentName = null
                        let sensorName = null
                        let unit = null
                        if (seriesName.indexOf(' - ') > -1) {
                            equipmentName = seriesName.split(' - ')[0]
                            sensorName = seriesName.split(' - ').slice(1).join(' - ')
                            unit = this.getUnit(sensorName, equipmentName)
                        }
                        points[seriesName] = {
                            value,
                            unit,
                            equipment_name: equipmentName,
                            sensor_name: sensorName,
                        }
                        latestValues[seriesName] = value
                    }
                }
                this.points = points
                this.latestValues = latestValues
                this.data = data
                this.templateRenderKey += 1
            } catch (error) {
                if (currentInc === this.reloadInc) {
                    this.error = 'Unable to load live HTML data: ' + error.message
                }
            } finally {
                if (currentInc === this.reloadInc) {
                    this.loading = false
                }
            }
        },
    },
}
</script>